/* global emailjs */

// EmailJS configuration – use your dashboard values
const EMAILJS_PUBLIC_KEY = "Y1hFSqaHZ9VfCM_30";
const EMAILJS_SERVICE_ID = "service_gqp5cob";
const EMAILJS_TEMPLATE_ID = "template_ig0hn9n";

// Rate limiting for form submission
let lastSubmissionTime = 0;
const SUBMISSION_COOLDOWN = 5000; // 5 seconds

// Resolves when EmailJS is ready (library loaded + initialized)
let emailjsReadyPromise = null;

function getEmailJSReadyPromise() {
  if (emailjsReadyPromise) return emailjsReadyPromise;
  emailjsReadyPromise = new Promise((resolve, reject) => {
    const deadline = Date.now() + 8000;
    const check = () => {
      if (typeof emailjs !== "undefined") {
        try {
          emailjs.init(EMAILJS_PUBLIC_KEY);
          resolve();
          return;
        } catch (e) {
          reject(e);
          return;
        }
      }
      if (Date.now() > deadline) {
        reject(new Error("EmailJS library did not load in time. Please refresh the page."));
        return;
      }
      setTimeout(check, 150);
    };
    check();
  });
  return emailjsReadyPromise;
}

// Input sanitization function
function sanitizeInput(input) {
  const div = document.createElement("div");
  div.textContent = input;
  return div.innerHTML;
}

// Initialize EmailJS
function initializeEmailJS() {
  if (typeof emailjs !== "undefined") {
    try {
      emailjs.init(EMAILJS_PUBLIC_KEY);
      console.log("✅ EmailJS initialized successfully");
    } catch (error) {
      console.error("❌ Failed to initialize EmailJS:", error);
    }
  } else {
    console.error("❌ EmailJS library not loaded");
  }
}

// Send email function
export const sendEmail = async (email, message) => {
  // Wait for EmailJS to be loaded and initialized (handles slow script load)
  await getEmailJSReadyPromise();

  return new Promise((resolve, reject) => {
    // Rate limiting check
    const currentTime = Date.now();
    if (currentTime - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      reject(
        new Error(
          "Please wait a few seconds before submitting another message.",
        ),
      );
      return;
    }

    // Additional validation
    if (!email || !message) {
      reject(new Error("Please fill in all fields."));
      return;
    }

    // Message length validation
    if (message.trim().length < 10) {
      reject(new Error("Message must be at least 10 characters long."));
      return;
    }

    if (message.trim().length > 1000) {
      reject(new Error("Message must be less than 1000 characters."));
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      reject(new Error("Please enter a valid email address."));
      return;
    }

    const sanitizedEmail = sanitizeInput(email);
    const sanitizedMessage = sanitizeInput(message);

    // EmailJS template parameters – use {{variable}} in your dashboard template
    // Sending common names so most templates work; your template can use any of these
    const templateParams = {
      from_email: sanitizedEmail,
      user_email: sanitizedEmail,
      reply_to: sanitizedEmail,
      email: sanitizedEmail,
      from_name: sanitizedEmail,
      name: sanitizedEmail,
      message: sanitizedMessage,
      notes: sanitizedMessage,
      body: sanitizedMessage,
      to_name: "Portfolio Owner",
    };

    console.log("Sending email with params:", {
      service: EMAILJS_SERVICE_ID,
      template: EMAILJS_TEMPLATE_ID,
      from: sanitizedEmail,
    });

    // Send email via EmailJS
    emailjs
      .send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY,
      )
      .then(function (response) {
        console.log("✅ Email sent successfully!", response);
        lastSubmissionTime = currentTime;
        resolve("Your message has been sent successfully!");
      })
      .catch(function (error) {
        console.error("❌ EmailJS Error:", error);
        console.error("Error Status:", error.status);
        console.error("Error Text:", error.text);
        console.error("Full Error:", JSON.stringify(error, null, 2));

        let errorMessage = "Failed to send message. Please try again later.";

        if (error.status === 412) {
          errorMessage =
            "Email service not fully set up. Fix in EmailJS:\n" +
            "1. Open https://dashboard.emailjs.com/ → Email Services\n" +
            "2. Open your service, then disconnect and reconnect your Gmail (or email) account\n" +
            "3. When reconnecting, allow “Send email on your behalf” (grant all requested permissions)\n" +
            "4. Save and try sending again. If it still fails, use a different Gmail or try the Outlook option.";
          console.error("⚠️ EmailJS 412 – Usually means linked email isn’t authorized to send:");
          console.error("   1. Dashboard: https://dashboard.emailjs.com/ → Email Services");
          console.error("   2. Edit service → Disconnect Gmail → Connect again");
          console.error("   3. In the Google popup, enable “Send email on your behalf” and complete sign-in");
          console.error("   4. Template (ID: " + EMAILJS_TEMPLATE_ID + ") can use: {{reply_to}}, {{message}}, {{from_name}}");
        } else if (error.status === 400) {
          errorMessage =
            "Invalid request format. Template variables may not match.";
          console.error(
            "⚠️ Check that your EmailJS template uses the correct variable names",
          );
        } else if (error.status === 401) {
          errorMessage = "Authentication failed. Public key may be invalid.";
          console.error("⚠️ Verify Public Key: " + EMAILJS_PUBLIC_KEY);
        } else if (error.status === 404) {
          errorMessage =
            "Service or template not found. Check your EmailJS dashboard.";
          console.error(
            "⚠️ Service '" + EMAILJS_SERVICE_ID + "' or Template '" + EMAILJS_TEMPLATE_ID + "' not found",
          );
        }

        reject(new Error(errorMessage));
      });
  });
};

// Initialize EmailJS when the module is imported
if (typeof window !== "undefined") {
  // Load EmailJS script if not already loaded
  if (!window.emailjs) {
    console.log("📦 Loading EmailJS library...");
    const script = document.createElement("script");
    script.src =
      "https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js";
    script.onload = () => {
      console.log("✅ EmailJS library loaded");
      initializeEmailJS();
    };
    script.onerror = () => {
      console.error("❌ Failed to load EmailJS library");
    };
    document.head.appendChild(script);
  } else {
    initializeEmailJS();
  }
}